import { ClientApplication } from '../../models/applications.model';
import { ClientPersonalDetails } from '../../models/client/personal.model';
import { FactFindQuestionDto, SectionType } from '../../models/fact-find-api.model';

const JOINT_QUESTION_PROPERTY_NAME = 'IsJoint';
const FALLBACK_PARTNER_LABEL = 'your co-applicant';
const BANKING_INHERITED_STATUS_MESSAGE =
  'This bank account follows the same applicants as your mortgage application.';
const JOINT_QUESTION_SECTIONS = new Set<SectionType>([
  SectionType.Dependants,
  SectionType.ExistingProperties,
  SectionType.Expenditure,
  SectionType.NewMortgage,
]);

export function enrichJointQuestions(params: {
  sectionType: SectionType;
  questions: FactFindQuestionDto[];
  currentClientId: number;
  selectedApplication: ClientApplication | null;
  currentClientPersonalDetails?: ClientPersonalDetails | null;
}): FactFindQuestionDto[] {
  const {
    sectionType,
    questions,
    currentClientId,
    selectedApplication,
    currentClientPersonalDetails = null,
  } = params;

  if (sectionType === SectionType.Banking) {
    return enrichBankingQuestions({
      questions,
      currentClientId,
      selectedApplication,
      currentClientPersonalDetails,
    });
  }

  if (!JOINT_QUESTION_SECTIONS.has(sectionType)) {
    return questions;
  }

  const shouldShowJointQuestion = isSharedJointFactFind(
    selectedApplication,
    currentClientId,
    currentClientPersonalDetails
  );
  const partnerLabel = getPartnerDisplayName(selectedApplication, currentClientId);

  return questions.map((question) => {
    if (question.propertyName !== JOINT_QUESTION_PROPERTY_NAME) {
      return question;
    }

    return {
      ...question,
      shouldAskUser: shouldShowJointQuestion,
      microcopy: replacePartnerPlaceholder(question.microcopy, partnerLabel),
    };
  });
}

function enrichBankingQuestions(params: {
  questions: FactFindQuestionDto[];
  currentClientId: number;
  selectedApplication: ClientApplication | null;
  currentClientPersonalDetails?: ClientPersonalDetails | null;
}): FactFindQuestionDto[] {
  const {
    questions,
    currentClientId,
    selectedApplication,
    currentClientPersonalDetails = null,
  } = params;

  const shouldShowInheritedStatus = isSharedJointFactFind(
    selectedApplication,
    currentClientId,
    currentClientPersonalDetails
  );

  if (!shouldShowInheritedStatus) {
    return questions;
  }

  return questions.map((question) => ({
    ...question,
    microcopy: prependMicrocopy(question.microcopy, BANKING_INHERITED_STATUS_MESSAGE),
  }));
}

export function isSharedJointFactFind(
  selectedApplication: ClientApplication | null,
  currentClientId: number,
  currentClientPersonalDetails: ClientPersonalDetails | null = null
): boolean {
  if (!selectedApplication || selectedApplication.Clients.length < 2) {
    return false;
  }

  const currentClientIsOnApplication = selectedApplication.Clients.some(
    (client) => client.ClientId === currentClientId
  );

  if (!currentClientIsOnApplication) {
    return false;
  }

  const coApplicantIds = selectedApplication.Clients.filter(
    (client) => client.ClientId !== currentClientId
  ).map((client) => client.ClientId);

  if (coApplicantIds.length === 0) {
    return false;
  }

  const linkedClientIds = new Set<number>();

  if (typeof currentClientPersonalDetails?.PartnerId === 'number') {
    linkedClientIds.add(currentClientPersonalDetails.PartnerId);
  }

  for (const jointApplicant of currentClientPersonalDetails?.JointApplicants ?? []) {
    if (typeof jointApplicant?.ClientId === 'number') {
      linkedClientIds.add(jointApplicant.ClientId);
    }
  }

  return coApplicantIds.some((clientId) => linkedClientIds.has(clientId));
}

export function getPartnerDisplayName(
  selectedApplication: ClientApplication | null,
  currentClientId: number
): string {
  const partner = selectedApplication?.Clients.find((client) => client.ClientId !== currentClientId);
  const partnerName = [partner?.FirstName, partner?.Surname].filter(Boolean).join(' ').trim();

  return partnerName || FALLBACK_PARTNER_LABEL;
}

export function toBooleanAnswer(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'yes' || normalized === 'true') {
      return true;
    }

    if (normalized === 'no' || normalized === 'false') {
      return false;
    }
  }

  return null;
}

function replacePartnerPlaceholder(microcopy: string | null, partnerLabel: string): string | null {
  if (!microcopy) {
    return microcopy;
  }

  return microcopy.split('{partner}').join(partnerLabel);
}

function prependMicrocopy(microcopy: string | null, prefix: string): string {
  if (!microcopy || microcopy.trim() === '') {
    return prefix;
  }

  if (microcopy.includes(prefix)) {
    return microcopy;
  }

  return `${prefix} ${microcopy}`;
}
